<template>
  <section class="screen" :class="modifiers">
    <header class="screen__header">
      <div v-if="$slots.title || title" class="screen__title">
        <BaseTitle
          v-if="title"
          :text="title"
          size="m"
          is-up
        />
        <slot name="title" />
      </div>
      <div v-if="$slots.actions || isCartButtonVisible" class="screen__actions">
        <slot name="actions" />
        <BaseButton
          v-if="isCartButtonVisible"
          :href="cartLink"
          :text="ticketsCountText"
          size="xs"
          slot="actions"
        />
      </div>
    </header>
    <div
      v-if="$slots.middle"
      class="screen__content screen__content_middle"
    >
      <slot name="middle" />
    </div>
    <div v-else class="screen__content">
      <slot />
    </div>
    <div
      v-if="isPending"
      class="screen__mask"
    >
      <BaseSpinner :size="48" :stroke="4" class="screen__spinner" />
      {{ pendingText }}
    </div>
    <slot name="root" />
  </section>
</template>

<script>
import { mapGetters } from 'vuex';

import BaseTitle from '@/components/BaseTitle.vue';
import BaseSpinner from '@/components/BaseSpinner.vue';
import BaseButton from '@/components/BaseButton.vue';

export default {
  name: 'BaseScreen',
  components: {
    BaseTitle,
    BaseSpinner,
    BaseButton,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    pendingText: {
      type: String,
      default() {
        return this.$t('pending.operation');
      },
    },
    isHeaderHidden: {
      type: Boolean,
      default: false,
    },
    isFooterHidden: {
      type: Boolean,
      default: false,
    },
    isPending: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('cart', [
      'ticketsCount',
    ]),
    modifiers() {
      return {
        screen_header_hidden: this.isHeaderHidden,
        screen_footer_hidden: this.isFooterHidden,
      };
    },
    ticketsCountText() {
      return this.$tc('pluralization.ticket', this.ticketsCount);
    },
    cartLink() {
      const { cart } = this.$route.meta || {};
      return cart ? this.$router.options.paths.cart : '';
    },
    isCartButtonVisible() {
      return Boolean(this.ticketsCount > 0 && this.cartLink);
    },
  },
};
</script>

<style lang="stylus">
@keyframes slide-out
  0%
    opacity 1
    transform translate3d(0, 0, 0)
  100%
    opacity 0
    transform translate3d(-50%, 0, 0)

@keyframes slide-out-back
  0%
    opacity 1
    transform translate3d(0, 0, 0)
  100%
    opacity 0
    transform translate3d(50%, 0, 0)

@keyframes slide-in
  0%
    opacity 0
    transform translate3d(50%, 0, 0)
  100%
    opacity 1
    transform translate3d(0, 0, 0)

@keyframes slide-in-back
  0%
    opacity 0
    transform translate3d(-50%, 0, 0)
  100%
    opacity 1
    transform translate3d(0, 0, 0)

.screen
  display flex
  flex-direction column
  width 100%
  height 100%
  overflow hidden
  background-color #fff

  &-slide-leave-active
    animation slide-out 500ms ease both
    .app_back_move &
      animation-name slide-out-back

  &-slide-enter-active
    animation slide-in 500ms ease both
    .app_back_move &
      animation-name slide-in-back

  &-slide-enter-active
  &-slide-leave-active
    abs top left right bottom
    overflow hidden
    pointer-events none
    *
      animation-play-state paused !important

  &__header
    position relative
    display flex
    align-items center
    justify-content center
    height $header_height
    padding-left $side_padding
    padding-right @padding-left
    ~/_header_hidden &
      visibility hidden

  &__content
    flex 1
    min-height 0
    padding-top 0.5em
    padding-bottom @padding-top + $footer_height
    padding-left $side_padding
    padding-right $side_padding
    &_middle
      display flex
      align-items center
      justify-content center
    ~/_footer_hidden &
      padding-top 0
      padding-bottom $side_padding

  &__actions
    abs top bottom right
    display flex
    align-items center
    padding-right $side_padding
    z-index 1
    .button
      margin-left 0.5em

  &__overlay
    fix top left right bottom
    z-index 100

  &__mask
    fix top left right bottom
    display flex
    align-items center
    justify-content center
    background-color alpha(#fff, 96%)
    font-size 1.5em
    color $c_text
    z-index 200

  &__spinner
    color $c_bright

  &__offset
    margin-right ($side_padding * -1)
    margin-left ($side_padding * -1)

  &__offset-right
    margin-right ($side_padding * -1)

  &__offset-left
    margin-left ($side_padding * -1)

  &__strech
    height 100%

  &__logo
    width 3em
    height @width
    margin 0 auto
    padding 0
    background none
    border 0
    outline none

  &__form
    max-width 44em
    margin 0 auto
    padding 2em 0
    &_no_padding
      padding 0

  &__timer
    abs top left right bottom
    display flex
    align-items center
    justify-content center
    background-color #fff
    font-size 1.4em
    font-weight 900
    z-index 1
</style>
